import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Resource } from '../../models/resource';
import { ResourceService } from '../../services/resource.service';
import { AuthService } from '../../services/auth.service';
import { ChoirResourceDialogComponent } from './choir-resource-dialog.component';
import { ResponsiveService } from '../../core/responsive.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'], // Updated to .scss
  standalone: false
})
export class HomepageComponent implements OnInit, OnDestroy {
  choirResources: Resource[] = [];
  choirResourceForm!: FormGroup;
  showChoirResourceForm = false;
  selectedChoirResource: Resource | null = null;
  selectedFile: File | null = null;
  currentUserIsAdmin: boolean = false;
  displayedColumns: string[] = []; // Initialize as empty
  isHandset$!: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
    private resourceService: ResourceService,
    private fb: FormBuilder,
    private authService: AuthService,
    private dialog: MatDialog,
    private responsive: ResponsiveService
  ) {}

  ngOnInit(): void {
    this.initChoirResourceForm();
    this.loadChoirResources();
    this.currentUserIsAdmin = this.authService.isCurrentUserAdmin();
  const desktopAdminCols = ['name', 'resourceType', 'path', 'actions'];
  const desktopUserCols = ['name', 'resourceType', 'path'];
    // Default to desktop before responsive observable emits
    this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;

    // Hook responsive layout
    this.isHandset$ = this.responsive.isHandset$;
    this.isHandset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isHandset => {
        if (isHandset) {
          // Handset: show Resource Name and Resource Type
          this.displayedColumns = ['name', 'resourceType'];
        } else {
          this.displayedColumns = this.currentUserIsAdmin ? desktopAdminCols : desktopUserCols;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initChoirResourceForm(): void {
    this.choirResourceForm = this.fb.group({
      name: ['', Validators.required],
      path: ['', Validators.required],
      isHyperlink: [false],
      resourceType: ['', Validators.required], // Editable field
      resourceEntity: ['Choir', Validators.required] // Fixed value
    });
  }

  loadChoirResources(): void {
    console.log('Loading choir resources...');
    this.resourceService.getResourcesByType('Choir').subscribe({
      next: (resources) => {
        this.choirResources = resources.filter(
          (resource) => resource.resourceEntity === 'Choir'
        );
  this.sortChoirResourcesByName();
        console.log('Choir resources loaded:', this.choirResources);
      },
      error: (error) => {
        console.error('Error loading choir resources:', error);
      }
    });
  }

  addChoirResource(): void {
    const dialogRef = this.dialog.open(ChoirResourceDialogComponent, {
      data: { resource: null, viewMode: false },
      width: '600px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((newResource: Resource | null) => {
      if (!newResource) {
        return;
      }

      const file: File | undefined = (newResource as any).file;
      const isHyperlink = !!newResource.isHyperlink;
      let basePath = (newResource.path || '').trim().replace(/^\/+/, '');
      basePath = basePath.replace(/^choir\//i, ''); // avoid double 'choir/'
      if (!isHyperlink && !file) {
        alert('Please choose a file to upload for a non-hyperlink resource.');
        return;
      }
      const fullPath = !isHyperlink && file ? `choir/${basePath}/${file.name}` : newResource.path || '';

      const payload: Resource = {
  id: 0,
        name: (newResource.name && newResource.name.trim().length > 0) ? newResource.name : (file ? file.name : newResource.name),
        path: fullPath,
        isHyperlink,
        resourceEntity: 'Choir',
        resourceType: newResource.resourceType
      };

      this.resourceService.createResource(payload).subscribe({
        next: () => {
          console.log('Choir resource added successfully');
          if (!isHyperlink && file) {
            const formData = new FormData();
            formData.append('file', file);
            this.resourceService.uploadResource(formData, payload.path).subscribe({
              next: () => {
                console.log('File uploaded successfully');
                this.loadChoirResources();
              },
              error: (error) => {
                console.error('Error uploading file:', error);
                this.loadChoirResources();
              }
            });
          } else {
            this.loadChoirResources();
          }
        },
        error: (error) => {
          console.error('Error adding choir resource:', error);
        }
      });
    });
  }

  editChoirResource(resource: Resource): void {
    const dialogRef = this.dialog.open(ChoirResourceDialogComponent, {
      data: { resource, viewMode: false }, // Pass the resource to the dialog
      width: '600px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((updatedResource: Resource | null) => {
      if (!updatedResource) {
        return;
      }

      const file: File | undefined = (updatedResource as any).file;
      const isHyperlink = !!updatedResource.isHyperlink;
      let basePath = (updatedResource.path || '').trim().replace(/^\/+/, '');
      basePath = basePath.replace(/^choir\//i, '');
      const fullPath = !isHyperlink && file ? `choir/${basePath}/${file.name}` : updatedResource.path || '';

      const payload: Resource = {
        id: updatedResource.id,
        name: (updatedResource.name && updatedResource.name.trim().length > 0) ? updatedResource.name : (file ? file.name : updatedResource.name),
        path: fullPath,
        isHyperlink,
        resourceEntity: 'Choir',
        resourceType: updatedResource.resourceType
      };

  const updateId = payload.id as number;
  this.resourceService.updateResource(updateId, payload).subscribe({
        next: () => {
          console.log('Choir resource updated successfully');
          if (!isHyperlink && file) {
            const formData = new FormData();
            formData.append('file', file);
            this.resourceService.uploadResource(formData, payload.path).subscribe({
              next: () => {
                console.log('File uploaded successfully');
                this.loadChoirResources();
              },
              error: (error) => {
                console.error('Error uploading file:', error);
                this.loadChoirResources();
              }
            });
          } else {
            this.loadChoirResources();
          }
        },
        error: (error) => {
          console.error('Error updating choir resource:', error);
        }
      });
    });

  }

  saveChoirResource(): void {
    if (this.choirResourceForm.valid) {
      const resourceData: Resource = {
        ...this.choirResourceForm.value,
  id: this.selectedChoirResource?.id ?? 0,
        songId: null,
        composerId: null,
        eventId: null
      };

      console.log('Saving choir resource:', resourceData);

      if (!this.choirResourceForm.get('isHyperlink')?.value) {
        resourceData.path = 'choir/' + this.choirResourceForm.get('path')?.value + "/" + this.selectedFile?.name;
      };

      console.log('path = ', resourceData.path);
    
      if (this.selectedChoirResource) {
  this.resourceService.updateResource(this.selectedChoirResource!.id as number, resourceData).subscribe({
          next: () => {
            console.log('Choir resource updated successfully');

            if (!this.choirResourceForm.get('isHyperlink')?.value && this.selectedFile) {
              this.uploadChoirResourceFile(resourceData.path);
            } else {
              this.loadChoirResources();
              this.closeChoirResourceForm();
            }
          },
          error: (error) => {
            console.error('Error updating choir resource:', error);
          }
        });
      } else {
        this.resourceService.createResource(resourceData).subscribe({
          next: () => {
            console.log('Choir resource added successfully');

            if (!this.choirResourceForm.get('isHyperlink')?.value && this.selectedFile) {
              this.uploadChoirResourceFile(resourceData.path);
            } else {
              this.loadChoirResources();
              this.closeChoirResourceForm();
            }
          },
          error: (error) => {
            console.error('Error adding choir resource:', error);
          }
        });
      }
    } else {
      console.warn('Form is invalid:', this.choirResourceForm.value);
    }
  }

  private uploadChoirResourceFile(path: string): void {
    if (this.selectedFile) {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      this.resourceService.uploadResource(formData, path).subscribe({
        next: () => {
          console.log('File uploaded successfully');
          this.loadChoirResources();
          this.closeChoirResourceForm();
        },
        error: (error) => {
          console.error('Error uploading file:', error);
        }
      });
    }
  }

  deleteChoirResource(id: number): void {
    if (confirm('Are you sure you want to delete this choir resource?')) {
      console.log('Deleting choir resource with ID:', id);
      this.resourceService.deleteResource(id).subscribe({
        next: () => {
          console.log('Choir resource deleted successfully');
          this.loadChoirResources();
        },
        error: (error) => {
          console.error('Error deleting choir resource:', error);
        }
      });
    }
  }

  onChoirResourceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      console.log('Selected file:', this.selectedFile);
    }
  }

  closeChoirResourceForm(): void {
    this.showChoirResourceForm = false;
    this.selectedChoirResource = null;
    this.choirResourceForm.reset();
    this.selectedFile = null;
  }

  onChoirResourceClick(event: Event, resource: Resource): void {
    event.preventDefault();
    event.stopPropagation();

    if (resource.isHyperlink) {
      window.open(resource.path, '_blank');
    } else {
      this.downloadChoirResource(resource);
    }
  }

  private downloadChoirResource(resource: Resource): void {
    if (!resource.path) {
      console.error('Resource path is empty or invalid:', resource);
      return;
    }

    this.resourceService.downloadResourceResponse(resource.path).subscribe({
      next: (res) => {
        const blob = res.body as Blob;
        if (!blob || blob.size === 0) {
          console.error('Downloaded file is empty. Check the backend response.');
          return;
        }
        const cd = res.headers.get('Content-Disposition');
        const fallbackName = resource.path.split('/')?.pop() || resource.name || 'download';
        const filename = this.extractFilename(cd) || fallbackName;
        const inferredType = this.inferMimeType(filename) || blob.type || 'application/octet-stream';
        const displayBlob = blob.type && blob.type !== '' ? blob : new Blob([blob], { type: inferredType });
        const viewable = inferredType.startsWith('image/') || inferredType === 'application/pdf' || inferredType.startsWith('text/') || inferredType === 'application/json' || inferredType.startsWith('audio/') || inferredType.startsWith('video/');
        const objectUrl = window.URL.createObjectURL(displayBlob);
        if (viewable) {
          const win = window.open(objectUrl, '_blank');
          if (!win) {
            console.warn('Popup blocked. Please allow popups for this site.');
          }
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
        } else {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      },
      error: (error) => {
        console.error('Error downloading choir resource:', error);
      }
    });
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const filenameStarMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition);
    if (filenameStarMatch && filenameStarMatch[1]) {
      try { return decodeURIComponent(filenameStarMatch[1]); } catch { /* ignore */ }
    }
    const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return filenameMatch && filenameMatch[1] ? filenameMatch[1] : null;
  }

  private inferMimeType(fileName: string): string {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'webp': return 'image/webp';
      case 'svg': return 'image/svg+xml';
      case 'pdf': return 'application/pdf';
      case 'txt': return 'text/plain';
      case 'html':
      case 'htm': return 'text/html';
      case 'json': return 'application/json';
      case 'mp3': return 'audio/mpeg';
      case 'wav': return 'audio/wav';
      case 'mp4': return 'video/mp4';
      case 'mov': return 'video/quicktime';
      default: return 'application/octet-stream';
    }
  }

  // Sort resources by first column (Name), ascending, case-insensitive
  private sortChoirResourcesByName(): void {
    this.choirResources.sort((a, b) => {
      const an = (a?.name ?? '').toString().trim().toLowerCase();
      const bn = (b?.name ?? '').toString().trim().toLowerCase();
      return an.localeCompare(bn);
    });
  }
}
